import { Link, useNavigate } from 'react-router-dom';
import { FaBook, FaCalendarAlt, FaBoxes, FaCheck, FaStar, FaArrowLeft, FaGraduationCap, FaBookOpen } from 'react-icons/fa';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';

function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showBundleChoiceModal, setShowBundleChoiceModal] = useState(false);
  const [showSubscriptionPaymentModal, setShowSubscriptionPaymentModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };
  
  const handleSubscriptionClick = () => {
    if (!user) {
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    setShowSubscriptionPaymentModal(true);
  };

  const handlePerContentClick = () => {
    if (!user) {
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    navigate('/courses');
  };

  const handleBundleClick = () => {
    if (!user) {
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    setShowBundleChoiceModal(true);
  };

  const handleSubscriptionOnlinePayment = async () => {
    try {
      setProcessing(true);
      const { checkoutUrl } = await apiService.createSubscriptionCheckout();
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Subscription checkout error:', err);
      showNotification('فشلت عملية إنشاء الدفع. يرجى المحاولة لاحقاً.', 'error');
      setProcessing(false);
    }
  };

  const handleSubscriptionManualPayment = () => {
    setShowSubscriptionPaymentModal(false);
    navigate('/manual-payment/subscription');
  };
  
  const pricingPlans = [
    {
      id: 'per-content',
      icon: <FaBook className="text-4xl" />,
      title: 'شراء حسب المحتوى',
      subtitle: 'ادفع فقط للمحتوى اللي تحتاجه',
      price: '1500',
      priceLabel: 'يبدأ من',
      currency: 'دج',
      features: [
        'وصول مدى الحياة للمحتوى',
        'تحديثات مجانية للمحتوى',
        'تتبع تقدمك في التعلم',
        'محتوى بالدارجة الجزائرية'
      ],
      buttonText: 'شوف المحتوى',
      buttonAction: handlePerContentClick,
      color: 'blue',
      recommended: false
    },
    {
      id: 'subscription',
      icon: <FaCalendarAlt className="text-4xl" />,
      title: 'اشتراك شهري',
      subtitle: 'وصول كامل لكل المحتوى التعليمي',
      price: '4900',
      priceLabel: 'شهرياً',
      currency: 'دج',
      features: [
        'وصول كامل لكل المحتوى (20+ محتوى)',
        'محتوى جديد كل شهر',
        'وصول لكل الدورات والقصص',
        'تتبع تقدمك في كل المحتويات',
        'إلغاء الاشتراك في أي وقت'
      ],
      buttonText: 'ابدأ الآن',
      buttonAction: handleSubscriptionClick,
      color: 'green',
      recommended: true,
      badge: 'الأكثر طلباً'
    },
    {
      id: 'bundle',
      icon: <FaBoxes className="text-4xl" />,
      title: 'حزمة ذكية مخصصة',
      subtitle: 'اختار حتى 5 محتويات بخصم كبير',
      price: '2,850',
      priceLabel: 'يبدأ من',
      currency: 'دج',
      features: [
        'اختار حتى 5 محتويات',
        'خصم تلقائي حسب العدد',
        'وصول مدى الحياة',
        '2 محتويات: خصم 10%',
        '5 محتويات: خصم 25%',
        'تحديثات مجانية دائمة'
      ],
      buttonText: 'ابدأ الاختيار',
      buttonAction: handleBundleClick,
      color: 'blue',
      recommended: false,
      badge: 'توفير ذكي'
    }
  ];

  const getColorClasses = (color, recommended) => {
    const colors = {
      blue: {
        icon: 'from-blue-500 to-blue-600',
        badge: 'bg-blue-500',
        border: 'border-blue-200 hover:border-blue-400',
        button: 'from-primary to-primary-dark hover:from-primary-dark hover:to-primary'
      },
      green: {
        icon: 'from-primary to-primary-dark',
        badge: 'bg-primary',
        border: 'border-primary/30 hover:border-primary',
        button: 'from-primary to-primary-dark hover:from-primary-dark hover:to-primary'
      }
    };
    return colors[color];
  };

  return (
    <div className="relative z-10 min-h-screen bg-gradient-to-b from-white via-gray-50 to-white py-24 overflow-x-hidden">
      {/* Blue gradient blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-blue-500/25 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-indigo-400/15 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full mb-6 font-semibold text-sm">
            <FaStar />
            <span>الأسعار</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-text mb-6 leading-tight">
            خطط <span className="text-primary">الأسعار</span>
          </h1>
          <p className="text-xl md:text-2xl text-text2 max-w-3xl mx-auto leading-relaxed">
            اختار الخطة اللي تناسبك. كل الخطط تشمل وصول كامل ودعم فني
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-[1200px] mx-auto mb-20">
          {pricingPlans.map((plan) => {
            const colorClasses = getColorClasses(plan.color, plan.recommended);
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-2 shadow-xl border-2 flex flex-col ${
                  plan.recommended 
                    ? 'border-primary shadow-2xl' 
                    : 'border-gray-200 hover:border-primary/50'
                }`}
              >
                <div className="pt-8 px-8 pb-8 flex flex-col flex-grow">
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-br ${colorClasses.icon} rounded-2xl flex items-center justify-center text-white shadow-lg transform transition-transform duration-300 hover:scale-110 hover:rotate-6`}>
                      {plan.icon}
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="text-3xl font-black text-text text-center mb-2">
                    {plan.title}
                  </h2>
                  <p className="text-text2 text-center mb-8 text-lg">
                    {plan.subtitle}
                  </p>

                  {/* Price */}
                  <div className="text-center mb-8 bg-gray-50 rounded-2xl p-6">
                    <div className="text-sm text-text3 mb-2">{plan.priceLabel}</div>
                    <div className="flex items-center justify-center gap-2">
                      <span className={`text-5xl font-black bg-gradient-to-r ${colorClasses.icon} bg-clip-text text-transparent`}>
                        {plan.price}
                      </span>
                      <span className="text-2xl font-bold text-text3">{plan.currency}</span>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-4 mb-8 flex-grow">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className={`w-6 h-6 bg-gradient-to-br ${colorClasses.icon} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <FaCheck className="text-white text-xs" />
                        </div>
                        <span className="text-text2 text-lg">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={plan.buttonAction}
                    className={`group flex items-center justify-center gap-2 w-full bg-gradient-to-r ${colorClasses.button} text-white px-6 py-5 rounded-2xl font-black text-lg transition-all duration-300 text-center shadow-xl hover:shadow-2xl`}
                  >
                    <span>{plan.buttonText}</span>
                    <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bundle Discount Table */}
        <div className="max-w-3xl mx-auto mb-20">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-6 md:p-8 border-2 border-blue-200 shadow-xl">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-black text-text mb-2">جدول الخصومات للحزمة المخصصة</h3>
              <p className="text-text2">كل ما زدت محتويات، كل ما زاد الخصم!</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { count: 2, original: 3000, discounted: 2700, discount: 10 },
                { count: 3, original: 4500, discounted: 3825, discount: 15 },
                { count: 4, original: 6000, discounted: 4800, discount: 20 },
                { count: 5, original: 7500, discounted: 5625, discount: 25 }
              ].map((item) => (
                <div key={item.count} className="bg-white rounded-2xl p-4 shadow-md border-2 border-blue-100 hover:border-blue-300 transition-all">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-black text-text text-lg">{item.count} محتويات</div>
                      <div className="text-sm text-text3 line-through">{item.original} دج</div>
                    </div>
                    <div className="text-left">
                      <div className="text-2xl font-black bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{item.discounted} دج</div>
                      <div className="text-xs font-bold text-green-600">خصم {item.discount}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full mb-6 font-semibold text-sm">
              <i className="fas fa-question-circle"></i>
              <span>الأسئلة الشائعة</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-text mb-4">
              أسئلة شائعة
            </h2>
            <p className="text-xl text-text2">
              اجوبة على اكثر الاسئلة لي كاينين على المنصة والاسعار
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                icon: 'fas fa-sync-alt',
                color: 'from-blue-500 to-indigo-600',
                q: 'نقدر نبدل الخطة تاعي من بعد؟',
                a: 'ايه، تنجم تبدل من أي خطة لأي خطة وقت ما تحب. الدراهم اللي دفعتها في الخطة القديمة تتحسب.'
              },
              {
                icon: 'fas fa-gift',
                color: 'from-purple-500 to-pink-600',
                q: 'كيفاش تخدم الحزمة المخصصة؟',
                a: 'اختار من 2 حتى 5 محتويات، والخصم يزيد كل ما زدت المحتوى. 2 محتويات = 5% خصم، حتى 5 محتويات = 20% خصم.'
              },
              {
                icon: 'fas fa-credit-card',
                color: 'from-green-500 to-emerald-600',
                q: 'كيفاش ندفع؟',
                a: 'تنجم تدفع عن طريق CIB/EDAHABIA أونلاين، ولا عن طريق CCP (حوالة بريدية) يدوياً.'
              },
              {
                icon: 'fas fa-layer-group',
                color: 'from-orange-500 to-red-600',
                q: 'نقدر نشري حزمة ومن بعد نشترك؟',
                a: 'ايه، المحتوى اللي شريتو في الحزمة يبقى ملكك للأبد، والاشتراك يعطيك وصول للباقي.'
              }
            ].map((faq, index) => (
              <div key={index} className="group bg-white rounded-3xl p-6 md:p-8 shadow-lg border-2 border-gray-100 hover:border-primary/30 transition-all hover:-translate-y-1">
                <div className="flex items-start gap-6">
                  <div className={`w-14 h-14 bg-gradient-to-br ${faq.color} rounded-2xl flex items-center justify-center text-white text-xl flex-shrink-0 shadow-lg transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}>
                    <i className={faq.icon}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-black text-text mb-3">
                      {faq.q}
                    </h3>
                    <p className="text-lg text-text2 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-br from-primary via-primary-dark to-secondary rounded-3xl p-8 md:p-12 lg:p-16 shadow-2xl max-w-4xl mx-auto relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.2) 0%, transparent 50%)`
              }}></div>
            </div>

            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                جاهز باش تبدأ؟
              </h2>
              <p className="text-xl md:text-2xl text-white/95 mb-10 leading-relaxed">
                انضم لآلاف المتعلمين اللي كيتعلمو معانا درك!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 bg-white text-primary px-10 py-5 rounded-2xl font-black text-lg hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
                >
                  <span>سجل مجاناً</span>
                  <FaArrowLeft />
                </Link>
                <Link
                  to="/courses"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all duration-300"
                >
                  <FaBook />
                  <span>شوف الدورات</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bundle Choice Modal */}
      {showBundleChoiceModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-primary-dark text-white p-8">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-3xl font-black mb-2 flex items-center gap-3">
                    <FaBoxes className="text-3xl" />
                    اختار نوع المحتوى
                  </h3>
                  <p className="text-white/90 text-lg">تحب تختار دورات ولا قصص مصورة؟</p>
                </div>
                <button
                  onClick={() => setShowBundleChoiceModal(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <i className="fas fa-times text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Courses Option */}
                <button
                  onClick={() => {
                    setShowBundleChoiceModal(false);
                    navigate('/courses?mode=bundle');
                  }}
                  className="group relative bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 hover:border-blue-400 rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <FaGraduationCap className="text-white text-3xl" />
                    </div>
                    <h4 className="text-2xl font-black text-text mb-2">الدورات الرقمية</h4>
                    <p className="text-text2 mb-4">اختار من الدورات التعليمية التفاعلية</p>
                    <div className="inline-flex items-center gap-2 text-primary font-bold group-hover:gap-3 transition-all">
                      <span>ابدأ الاختيار</span>
                      <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>

                {/* Storybooks Option */}
                <button
                  onClick={() => {
                    setShowBundleChoiceModal(false);
                    navigate('/storybooks?mode=bundle');
                  }}
                  className="group relative bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 hover:border-blue-400 rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <FaBookOpen className="text-white text-3xl" />
                    </div>
                    <h4 className="text-2xl font-black text-text mb-2">القصص المصورة</h4>
                    <p className="text-text2 mb-4">اختار من القصص التفاعلية الممتعة</p>
                    <div className="inline-flex items-center gap-2 text-primary font-bold group-hover:gap-3 transition-all">
                      <span>ابدأ الاختيار</span>
                      <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-center text-text2 text-sm">
                  <i className="fas fa-info-circle text-primary ml-2"></i>
                  يمكنك اختيار من 2 إلى 5 محتويات من نفس النوع وتحصل على خصم تلقائي!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Payment Modal */}
      {showSubscriptionPaymentModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSubscriptionPaymentModal(false)}
        >
          <div 
            className="bg-white rounded-3xl p-8 max-w-[500px] w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowSubscriptionPaymentModal(false)}
              className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 text-2xl w-8 h-8 flex items-center justify-center transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>

            <h2 className="text-3xl font-black text-gray-800 mb-6">اختر طريقة الدفع</h2>
            
            <div className="bg-gray-50 p-6 rounded-2xl mb-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-gray-600">الخطة:</span>
                <span className="font-bold text-gray-800">اشتراك شهري</span>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-bold">المجموع:</span>
                  <span className="font-black text-primary text-3xl">4900 دج / شهر</span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <button
                onClick={handleSubscriptionOnlinePayment}
                disabled={processing}
                className="w-full bg-primary text-white px-6 py-4 rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <i className="fas fa-credit-card"></i>
                {processing ? 'جاري التحويل للدفع...' : 'دفع أونلاين'}
              </button>
              <p className="text-xs text-gray-600 text-center mt-2">
                سيتم تحويلك لصفحة الدفع الآمنة (CIB / EDAHABIA)
              </p>
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-gray-400">أو</span>
              </div>
            </div>

            <div>
              <button
                onClick={handleSubscriptionManualPayment}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-xl font-bold hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <i className="fas fa-money-bill-wave"></i>
                الدفع اليدوي عبر CCP (حوالة بريدية)
              </button>
              <p className="text-xs text-gray-600 text-center mt-2">
                للدفع عبر الحساب الجاري البريدي
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 animate-slideDown">
          <div className={`px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 min-w-[300px] max-w-md ${
            notification.type === 'success' ? 'bg-green-500 text-white' :
            notification.type === 'error' ? 'bg-red-500 text-white' :
            notification.type === 'warning' ? 'bg-yellow-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            <i className={`fas ${
              notification.type === 'success' ? 'fa-check-circle' :
              notification.type === 'error' ? 'fa-times-circle' :
              notification.type === 'warning' ? 'fa-exclamation-triangle' :
              'fa-info-circle'
            } text-2xl`}></i>
            <span className="text-lg">{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default PricingPage;
